import * as React from "react"
import { useShell } from "./context"
import { travelArrow, travelDots } from "./dotsTravel"
import { followKeyboard } from "./phoneKeyboard"

// (a finger: no field focused by itself, iOS would bring no keyboard up for it)
const coarse = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches
import { ContactForm, openContact } from "./Contact"

// The main menu (button, panel, work submenu). Ported from MainNavigation in
// the Framer WorkNavigation component; page changes go through the shell.
const ASSETS = {
    menu: "https://framerusercontent.com/images/HvOF7wMlvrRwFhXPXcCnvJVSYA.svg",
}
import { MENU } from "./settings"
const defaultCategories = MENU.categories

export function Menu({
    menuOpen,
    onOpen,
    mobile = false,
    menuSpeed = 2,
    menuFont,
    submenuFont,
    categories = defaultCategories,
    currentCategory = "Sports",
    workUrl = "/sports",
    feedUrl = "/feed",
    contactUrl = "/about",
    introRefs,
    introStyle,
    scale = 1,
}: any) {
    const menuButton = React.useRef<HTMLButtonElement | null>(null)
    const [menuSeen, setMenuSeen] = React.useState(false)
    const menuId = "nto-main-" + React.useId().replace(/:/g, "")
    const stop = (e: React.SyntheticEvent) => e.stopPropagation()
    const [subStage, setSubStage] = React.useState("main")
    const subTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
    const backButton = React.useRef<HTMLButtonElement>(null)
    const [pressedCategory, setPressedCategory] = React.useState<string | null>(
        null
    )
    const switchSubmenu = (show: boolean) => {
        if (subTimer.current) clearTimeout(subTimer.current)
        setSubStage(show ? "leaving" : "returning")
        subTimer.current = setTimeout(() => {
            setSubStage(show ? "categories" : "main")
            if (show) backButton.current?.focus({ preventScroll: true })
        }, 220)
    }
    React.useEffect(
        () => () => {
            if (subTimer.current) clearTimeout(subTimer.current)
        },
        []
    )
    // Get in touch: on a phone, this panel takes the form (closing the menu ends it)
    const [contact, setContact] = React.useState(false)
    // (sent: the panel closes in to the thank-you, as high as the menu's button)
    const [contactSent, setContactSent] = React.useState(false)
    React.useEffect(() => {
        if (!menuOpen) {
            const t = setTimeout(() => (setContact(false), setContactSent(false)), 919 / Math.max(0.25, menuSpeed) + 100)
            return () => clearTimeout(t)
        }
    }, [menuOpen])
    // (phones, the form open: when a field's tapped, iOS scrolls the page to
    // show it above the keyboard. The work pages snap to their sections, and
    // that snapped the page straight back, the form behind the keyboard again.
    // So, while the form's open, no snapping: html[data-contact-m], contact.css)
    // And the keyboard: the panel rises clear of it and goes down with it,
    // without iOS panning the screen about (phoneKeyboard.ts).
    const keyboard = React.useRef<ReturnType<typeof followKeyboard> | null>(null)
    React.useLayoutEffect(() => {
        const root = menuButton.current?.closest<HTMLElement>(".nto27")
        if (!mobile || !contact || !menuOpen || !root) return
        const html = document.documentElement
        html.setAttribute("data-contact-m", "")
        const k = (keyboard.current = followKeyboard(root))
        return () => {
            html.removeAttribute("data-contact-m")
            k.release()
            keyboard.current = null
        }
    }, [mobile, contact, menuOpen])
    // (a tap on the blur with the keyboard up: the keyboard goes, the panel
    // down with it; then the form closes, as ever, not all at once)
    const leaving = React.useRef(false)
    const leave = () => {
        const k = keyboard.current
        if (!k?.up()) return openMenu(false)
        if (leaving.current) return
        leaving.current = true
        k.dismiss().then(() => ((leaving.current = false), openMenu(false)))
    }
    // (closing from the thank-you: its cross's dots travel into the button's)
    React.useLayoutEffect(() => {
        if (menuOpen || !mobile || !contact) return
        // (sent: the thank-you's cross; left unsent: the send arrow)
        const dots = document.querySelector<HTMLElement>(contactSent ? ".n27-contact .ct-close" : ".n27-contact .ct-send")
        const root = dots?.closest<HTMLElement>(".nto27")
        if (!dots || !root || !menuButton.current) return
        if (contactSent) travelDots(dots, menuButton.current, root)
        else travelArrow(dots, menuButton.current, root)
    }, [menuOpen])
    const getInTouch = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        if (mobile) return setContact(true)
        // desktop: the button leaves the menu (which closes) and becomes the form
        openContact(e.currentTarget)
        openMenu(false)
    }
    const [veilIdle, setVeilIdle] = React.useState(true)
    // Match Home27: retain the backdrop throughout its closing transition.
    React.useEffect(() => {
        if (menuOpen) {
            setVeilIdle(false)
            return
        }
        const timer = setTimeout(
            () => setVeilIdle(true),
            919 / Math.max(0.25, menuSpeed) + 100
        )
        return () => clearTimeout(timer)
    }, [menuOpen, menuSpeed])

    React.useEffect(() => {
        if (menuOpen) {
            setMenuSeen(true)
            if (subTimer.current) clearTimeout(subTimer.current)
        } else {
            if (subTimer.current) clearTimeout(subTimer.current)
            subTimer.current = setTimeout(
                () => setSubStage("main"),
                919 / Math.max(0.25, menuSpeed) + 100
            )
        }
    }, [menuOpen, menuSpeed])
    const openMenu = (open: boolean) => onOpen(open)
    const shell = useShell()
    const goTo = (e: React.MouseEvent<HTMLAnchorElement>, href?: string) => {
        if (!href) return e.preventDefault()
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
            return
        openMenu(false)
        if (shell?.navigate(href)) e.preventDefault()
    }
    return (
        <div
            className="nto27 nto-shared-navigation sh-menu"
            data-menu={menuOpen}
            data-contact={(mobile && contact) || undefined}
            data-contact-sent={(mobile && contact && contactSent) || undefined}
            data-seen={menuSeen}
            data-idle={(veilIdle && !menuOpen) || undefined}
            data-mobile={mobile ? "true" : "false"}
            style={{
                position: "absolute",
                inset: 0,
                // Self-contained: the same fonts on every page, whatever it sits in.
                fontFamily: "NTO27Blubird, sans-serif",
                fontWeight: 600,
                pointerEvents: "none",
                ["--k" as any]: 1 / menuSpeed,
                ["--s" as any]: scale,
            }}
        >
            <div
                className="n27-veil"
                onClick={(e) => {
                    e.stopPropagation()
                    leave()
                }}
                style={{
                    visibility: veilIdle && !menuOpen ? "hidden" : "visible",
                }}
            />
            <div
                className="n27-wrap"
                onClick={stop}
                onWheel={stop}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        openMenu(false)
                        menuButton.current?.focus()
                    }
                }}
            >
                <nav
                    id={menuId}
                    className="n27-panel"
                    aria-label="Main navigation"
                    aria-hidden={!menuOpen}
                    style={{ visibility: menuSeen ? "visible" : "hidden" }}
                >
                    {mobile && contact && (
                        <div className="n27-contact">
                            <ContactForm variant="card" autoFocus={!coarse} onSent={() => setContactSent(true)} onClose={() => openMenu(false)} />
                        </div>
                    )}
                    <div className="n27-inner" data-submenu={subStage}>
                        <div
                            className="n27-main"
                            aria-hidden={subStage !== "main"}
                        >
                            <ul
                                className="n27-links"
                                style={{
                                    fontWeight: menuFont?.fontWeight || 700,
                                    fontFamily: menuFont?.fontFamily,
                                    fontStyle: menuFont?.fontStyle,
                                }}
                            >
                                {[
                                    ["Work", workUrl],
                                    ["About", contactUrl || "/about"],
                                    ["Feed", feedUrl || "/feed"],
                                ].map(([label, href], i) => (
                                    <li
                                        key={label}
                                        style={{ ["--o" as any]: 2 - i }}
                                    >
                                        {href ? (
                                            <a
                                                href={href}
                                                aria-haspopup={
                                                    label === "Work"
                                                        ? "menu"
                                                        : undefined
                                                }
                                                onClick={
                                                    label === "Work"
                                                        ? (e) => {
                                                              e.preventDefault()
                                                              switchSubmenu(
                                                                  true
                                                              )
                                                          }
                                                        : // (through the page change: the panel stops clicks reaching the page's own handling)
                                                          (e) => goTo(e, href)
                                                }
                                                tabIndex={
                                                    menuOpen &&
                                                    subStage === "main"
                                                        ? 0
                                                        : -1
                                                }
                                            >
                                                {label.toUpperCase()}
                                            </a>
                                        ) : (
                                            <span aria-disabled="true">
                                                {label.toUpperCase()}
                                            </span>
                                        )}
                                        <span
                                            className="n27-arrow"
                                            aria-hidden="true"
                                        >
                                            <i
                                                style={{
                                                    left: "var(--ax)",
                                                    top: "var(--ay)",
                                                    ["--d" as any]: "0ms",
                                                }}
                                            />
                                            <i
                                                style={{
                                                    left: 0,
                                                    top: "var(--ab)",
                                                    ["--d" as any]: "79ms",
                                                }}
                                            />
                                            <i
                                                style={{
                                                    left: 0,
                                                    top: 0,
                                                    ["--d" as any]: "168ms",
                                                }}
                                            />
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                className="n27-cta"
                                href={contactUrl}
                                onClick={getInTouch}
                                tabIndex={
                                    menuOpen && subStage === "main" ? 0 : -1
                                }
                                style={{ fontWeight: 700 }}
                            >
                                <span className="n27-cta-shape" />
                                <span className="n27-cta-move">
                                    <span className="n27-cta-line">
                                        {["Get", "in", "touch"].map((w, i) => (
                                            <span
                                                key={w}
                                                style={{ ["--o" as any]: i }}
                                            >
                                                {w}
                                            </span>
                                        ))}
                                    </span>
                                </span>
                                <span className="n27-dots">
                                    <i
                                        className="n27-dot"
                                        style={{
                                            left: 0,
                                            top: 0,
                                            ["--d" as any]:
                                                "calc(var(--k,1)*805ms)",
                                        }}
                                    />
                                    <i
                                        className="n27-dot"
                                        style={{
                                            left: 0,
                                            top: 12,
                                            ["--d" as any]:
                                                "calc(var(--k,1)*721ms)",
                                        }}
                                    />
                                    <i
                                        className="n27-dot"
                                        style={{
                                            left: 7,
                                            top: 6,
                                            ["--d" as any]:
                                                "calc(var(--k,1)*647ms)",
                                        }}
                                    />
                                </span>
                            </a>
                        </div>
                        <div
                            className="n27-submenu"
                            aria-hidden={subStage !== "categories"}
                        >
                            <button
                                ref={backButton}
                                className="n27-back"
                                aria-label="Back to main menu"
                                tabIndex={
                                    menuOpen && subStage === "categories"
                                        ? 0
                                        : -1
                                }
                                onClick={() => switchSubmenu(false)}
                            >
                                <span className="n27-back-arrow" aria-hidden="true">
                                    <i />
                                    <i />
                                    <i />
                                </span>
                                <span className="n27-back-label">WORK</span>
                            </button>
                            <nav
                                aria-label="Work categories"
                                className="n27-category-links"
                                data-pressed={pressedCategory || undefined}
                                style={{
                                    fontFamily:
                                        submenuFont?.fontFamily ||
                                        menuFont?.fontFamily,
                                    fontWeight: submenuFont?.fontWeight,
                                    fontStyle: submenuFont?.fontStyle,
                                }}
                            >
                                {categories.map((c: { label: string; href?: string; count?: number }, i: number) => {
                                    const label = c.label
                                    const destination = c.href || undefined
                                    return (
                                        <a
                                            key={label}
                                            href={destination}
                                            role="link"
                                            aria-disabled={
                                                !destination || undefined
                                            }
                                            tabIndex={
                                                menuOpen &&
                                                subStage === "categories"
                                                    ? 0
                                                    : -1
                                            }
                                            style={{ ["--item" as any]: i }}
                                            data-active={
                                                pressedCategory === label ||
                                                undefined
                                            }
                                            onPointerDown={() =>
                                                setPressedCategory(label)
                                            }
                                            onPointerUp={() =>
                                                setPressedCategory(null)
                                            }
                                            onPointerCancel={() =>
                                                setPressedCategory(null)
                                            }
                                            onPointerLeave={() =>
                                                setPressedCategory(null)
                                            }
                                            onClick={(e) =>
                                                goTo(e, destination)
                                            }
                                        >
                                            <span className="n27-cat-label">
                                                {label.toUpperCase()}
                                            </span>
                                            {/* the number of cases in the category */}
                                            {!!c.count && (
                                                <span className="n27-count" aria-label={`${c.count} ${c.count === 1 ? "case" : "cases"}`}>
                                                    {c.count}
                                                </span>
                                            )}
                                        </a>
                                    )
                                })}
                            </nav>
                        </div>
                    </div>
                </nav>
                <button
                    ref={(el) => {
                        menuButton.current = el
                        introRefs?.button?.(el)
                    }}
                    className="n27-navbtn"
                    aria-label={
                        menuOpen ? "Close navigation" : "Open navigation"
                    }
                    aria-controls={menuId}
                    aria-expanded={menuOpen}
                    onClick={() => openMenu(!menuOpen)}
                    style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: mobile ? 70 : 60,
                        height: 55,
                        borderRadius: 64,
                        background: "white",
                        ...introStyle,
                    }}
                >
                    <span className="n27-icon-dark">
                        <img
                            ref={introRefs?.icon}
                            style={introStyle}
                            src={ASSETS.menu}
                            width={17}
                            height={17}
                            alt=""
                        />
                    </span>
                </button>
            </div>
        </div>
    )
}
